/**
 * Shared analytics helper functions
 *
 * Common utilities used by both Phase 1 (SMBSignupModal) and Phase 2 (NewCustomerSignupModalOTP)
 * analytics tracking modules.
 */
import store from '@/store';
import { rateDisplay } from '@/utils';

/**
 * User status based on authentication and cookie presence
 * Standardized across Phase 1 and Phase 2
 */
export type UserStatus = 'logged_in' | 'returning' | 'new';

/**
 * UTM parameters captured from URL
 */
export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
}

/**
 * Determine user status based on authentication and cookie presence
 *
 * @returns 'logged_in' if user is authenticated,
 *          'returning' if of_rate_display cookie exists,
 *          'new' if no cookie and not authenticated
 */
export function getUserStatus(): UserStatus {
  const state = store.getState();
  const viewer = state.viewer;

  if (viewer?.id) {
    return 'logged_in';
  }

  const hasRateDisplayCookie = rateDisplay.has();
  if (hasRateDisplayCookie) {
    return 'returning';
  }

  return 'new';
}

/**
 * Capture UTM parameters from current URL
 *
 * @returns Object with all UTM parameters (null if not present)
 */
export function captureUTMParams(): UTMParams {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
  };
}

/**
 * Calculate duration between two timestamps
 *
 * @param startTime - Start timestamp (ISO string or Date)
 * @param endTime - End timestamp (ISO string or Date), defaults to now
 * @returns Duration in seconds
 */
export function calculateDuration(
  startTime: string | Date,
  endTime: string | Date = new Date()
): number {
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  return Math.round((end.getTime() - start.getTime()) / 1000);
}
